import { Component, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { APP_ROUTES } from "../../../contants/APP_ROUTES";
import { COMMON_EXPENSE_ARRAY } from "../../../contants/COMMON_EXPENSE_ARRAY";
import { PARAMS } from "../../../contants/PARAMS";
import { Helper } from "../../../helpers/Helper";
import { DailyTrip } from "../../../models/trips/dailyTrip";
import { DailyTripExpense } from "../../../models/trips/dailyTripExpense";
import { AlertService } from "../../../services/alert.service";
import { DailyTripService } from "../../../services/daily-trip.service";
import { ParameterService } from "../../../services/parameter.service";
import { CustomValidators } from "../../../validators/customValidators";
import { EDailyTripTypes as EDailyTripType } from "../../../enums/daily-trip-types";
import { TripService } from "../../../services/trip.service";
import { ETripType } from "../../../enums/trip-type";
import { QUERY_PARAMS } from "../../../contants/QUERY_PARAMS";

@Component({
  selector: "app-daily-trip-form",
  templateUrl: "./daily-trip-form.component.html",
  styleUrls: ["./daily-trip-form.component.css"],
})
export class DailyTripFormComponent implements OnInit {
  form: FormGroup;
  dailyTripExpenses: FormArray;

  dailyTrip: DailyTrip;

  commonExpenseTypes: string[] = COMMON_EXPENSE_ARRAY;
  totalLoaded: number = 0;
  totalEmpty: number = 0;  
  canEditTripDate: boolean = false;
  canUpdateTripType: boolean = true;
  hasReferencedBaseExpense: boolean = false;

  constructor(
    private _dailyTripService: DailyTripService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _alertService: AlertService,
    private _parameterService: ParameterService,
    private _tripService: TripService
  ) {}

  ngOnInit() {
    this.canEditTripDate = Helper.isManager();

    const id = this._activatedRoute.snapshot.params["id"];    

    this.dailyTrip = new DailyTrip();
    this.dailyTrip.dailyTripExpenses = [];
    this.dailyTrip.id = id;
    this.dailyTrip.bookKeeperId = Helper.currentUserId();

    this.form = this.createForm(this.dailyTrip);    

    if (id == 0) {
      this.dailyTrip.id = 0;
      this._parameterService
        .getCachedSystemParameterArray<string>(
          PARAMS.DAILY_TRIP_COMMON_EXPENSE,
          3600
        )
        .subscribe((result) => {
          if (result.length != 0) {
            this.commonExpenseTypes = result;
          }

          this.commonExpenseTypes.forEach((x) => {
            var expense = new DailyTripExpense();
            expense.id = 0;
            expense.expenseType = x;
            expense.amount = 0;

            this.addExpense(expense);
          });
        });

        const clientId = this._activatedRoute.snapshot.queryParams[QUERY_PARAMS.CLIENT_ID];
        const tripId = this._activatedRoute.snapshot.queryParams[QUERY_PARAMS.TRIP_ID];
        const containerId = this._activatedRoute.snapshot.queryParams[QUERY_PARAMS.CONTAINER_ID];
        this.form.patchValue({ 
          clientId: clientId,
          tripId: tripId,
          tripContainerDetailId: containerId
        });

        this.autofillForm();

    } else {
      this._dailyTripService.getDailyTrip(id).subscribe((result) => {
        if (result == undefined) {
          this.dailyTrip.id = 0;          
        } else {
          this.dailyTrip = result;
          this.canUpdateTripType = Helper.isManager();
          this.reloadForm();
        }
      });
    }
  }

  private reloadForm() {
    this.form = this.createForm(this.dailyTrip);
    if (this.dailyTrip.dailyTripExpenses !== undefined) {
      this.dailyTrip.dailyTripExpenses.forEach((x) => this.addExpense(x));
    }
    this.computeTotal();
  }

  private createForm(source: DailyTrip): FormGroup {
    this.dailyTripExpenses = new FormArray([]);
    var formGroup = new FormGroup({
      tripDate: new FormControl(source.tripDate, [Validators.required]),
      vehicleId: new FormControl(source.vehicleId, [Validators.required]),
      driverId: new FormControl(source.driverId, [Validators.required]),
      helperId: new FormControl(source.helperId, [Validators.required]),
      bookKeeperId: new FormControl(source.bookKeeperId, [Validators.required]),
      clientId: new FormControl(source.clientId),
      tripId: new FormControl(source.tripId, [Validators.required]),
      tripContainerDetailId: new FormControl(source.tripContainerDetailId, [
        Validators.required,
      ]),
      returnDestination: new FormControl(source.returnDestination, [
        Validators.maxLength(30),
      ]),
      pricePerLiter: new FormControl(source.pricePerLiter, [
        Validators.required,
      ]),
      liters: new FormControl(source.liters, [
        CustomValidators.validateCurrency,
      ]),
      karga: new FormControl(source.karga, [CustomValidators.validateCurrency]),
      soli: new FormControl(source.soli, [CustomValidators.validateCurrency]),
      dagSoli: new FormControl(source.dagSoli, [
        CustomValidators.validateCurrency,
      ]),
      areglo: new FormControl(source.areglo, [
        CustomValidators.validateCurrency,
      ]),
      escort: new FormControl(source.escort, [
        CustomValidators.validateCurrency,
      ]),
      shippingLineAgent: new FormControl(
        source.shippingLineAgent,
        Validators.maxLength(50)
      ),
      shippingLineFee: new FormControl(source.shippingLineFee, [
        CustomValidators.validateCurrency,
      ]),
      checkerName: new FormControl(
        source.checkerName,
        Validators.maxLength(50)
      ),
      cFee: new FormControl(source.cFee, [CustomValidators.validateCurrency]),
      tollGate: new FormControl(source.tollGate, [
        CustomValidators.validateCurrency,
      ]),
      manPower: new FormControl(source.manPower, [
        CustomValidators.validateCurrency,
      ]),
      singitPier2: new FormControl(source.singitPier2, [
        CustomValidators.validateCurrency,
      ]),
      remarks: new FormControl(source.remarks, Validators.maxLength(50)),
      remarksFee: new FormControl(source.remarksFee, [
        CustomValidators.validateCurrency,
      ]),
      lagay: new FormControl(source.allowance, [
        CustomValidators.validateCurrency,
      ]),
      cashDiesel: new FormControl(source.cashDiesel, [
        CustomValidators.validateCurrency,
      ]),
      litersEmpty: new FormControl(source.litersEmpty, [
        CustomValidators.validateCurrency,
      ]),
      pricePerLiterEmpty: new FormControl(source.pricePerLiterEmpty, [
        CustomValidators.validateCurrency,
      ]),
      cashDieselEmpty: new FormControl(source.cashDieselEmpty, [
        CustomValidators.validateCurrency,
      ]),
      tollGateEmpty: new FormControl(source.tollGateEmpty, [
        CustomValidators.validateCurrency,
      ]),
      xray: new FormControl(source.xray, [CustomValidators.validateCurrency]),
      dailyTripExpenses: this.dailyTripExpenses,
      abonoManPower: new FormControl(source.abonoManPower),
      abonoDagSoli: new FormControl(source.abonoDagSoli),
      abonoEscort: new FormControl(source.abonoEscort),
      
      dailyTripTypeId: new FormControl(source.dailyTripTypeId, [Validators.required]),
      tabs: new FormControl(source.tabs),
      tabsPenalty: new FormControl(source.tabsPenalty),
      abonoTabs: new FormControl(source.abonoTabs),
      abonoTabsPenalty: new FormControl(source.abonoTabsPenalty),
    });

    formGroup.valueChanges.subscribe(x => this.computeTotal());
    if(source.dailyTripTypeId == 0){
      formGroup.controls['tripContainerDetailId'].valueChanges.subscribe(x => this.autofillForm());
    }
    return formGroup;
  }

  private createExpenseForm(source?: DailyTripExpense): FormGroup {
    if (source == null || source == undefined) {
      source = new DailyTripExpense();
    }

    return new FormGroup({
      id: new FormControl(source.id),
      expenseType: new FormControl(source.expenseType),
      remarks: new FormControl(source.remarks, [Validators.maxLength(50)]),
      amount: new FormControl(source.amount, [
        Validators.required,
        CustomValidators.validateCurrency,
      ]),
    });
  }

  onFormSubmit(formValue) {
    //update daily trip item
    if (!this.form.valid) {
      this._alertService.showError("Form is invalid.");
      return;
    }

    this.dailyTrip.tripDate = formValue.tripDate;
    this.dailyTrip.vehicleId = formValue.vehicleId;
    this.dailyTrip.driverId = formValue.driverId;
    this.dailyTrip.helperId = formValue.helperId;
    this.dailyTrip.bookKeeperId = formValue.bookKeeperId;
    this.dailyTrip.tripId = formValue.tripId;
    this.dailyTrip.tripContainerDetailId = formValue.tripContainerDetailId;
    this.dailyTrip.returnDestination = formValue.returnDestination;
    this.dailyTrip.pricePerLiter = formValue.pricePerLiter;
    this.dailyTrip.dailyTripTypeId = Number(formValue.dailyTripTypeId);

    this.dailyTrip.liters = formValue.liters;
    this.dailyTrip.karga = formValue.karga;
    this.dailyTrip.soli = formValue.soli;
    this.dailyTrip.dagSoli = formValue.dagSoli;
    this.dailyTrip.areglo = formValue.areglo;
    this.dailyTrip.escort = formValue.escort;
    this.dailyTrip.shippingLineAgent = formValue.shippingLineAgent;
    this.dailyTrip.shippingLineFee = formValue.shippingLineFee;
    this.dailyTrip.checkerName = formValue.checkerName;
    this.dailyTrip.cFee = formValue.cFee;
    this.dailyTrip.tollGate = formValue.tollGate;
    this.dailyTrip.manPower = formValue.manPower;
    this.dailyTrip.singitPier2 = formValue.singitPier2;
    this.dailyTrip.remarks = formValue.remarks;
    this.dailyTrip.remarksFee = formValue.remarksFee;
    this.dailyTrip.allowance = formValue.lagay;
    this.dailyTrip.cashDiesel = formValue.cashDiesel;

    this.dailyTrip.tollGateEmpty = formValue.tollGateEmpty;
    this.dailyTrip.litersEmpty = formValue.litersEmpty;
    this.dailyTrip.pricePerLiterEmpty = formValue.pricePerLiterEmpty;
    this.dailyTrip.cashDieselEmpty = formValue.cashDieselEmpty;
    this.dailyTrip.xray = formValue.xray;

    this.dailyTrip.abonoDagSoli = formValue.abonoDagSoli;
    this.dailyTrip.abonoEscort = formValue.abonoEscort;
    this.dailyTrip.abonoManPower = formValue.abonoManPower;
      
    this.dailyTrip.tabs = formValue.tabs;
    this.dailyTrip.tabsPenalty = formValue.tabsPenalty;
    this.dailyTrip.abonoTabs = formValue.abonoTabs;
    this.dailyTrip.abonoTabsPenalty = formValue.abonoTabsPenalty;

    //update expenses
    this.dailyTrip.dailyTripExpenses = [];
    formValue.dailyTripExpenses.forEach((x) => {
      this.dailyTrip.dailyTripExpenses.push(x);
    });

    if (this.dailyTrip.id == undefined || this.dailyTrip.id == 0) {
      this.createDailyTrip();
    } else {
      this.updateDailyTrip();
    }
  }

  private createDailyTrip() {
    this._dailyTripService.addDailyTrip(this.dailyTrip).subscribe((result) => {
      let newItem = result.object;
      if (newItem != null) {
        this.dailyTrip = newItem;
        this._alertService.showSuccess("Success");
        this.reloadForm();
      } else {
        this._alertService.displayErrorResult(result.errorCollection);
      }
    });
  }

  private updateDailyTrip() {
    this._dailyTripService
      .updateDailyTrip(this.dailyTrip)
      .subscribe((result) => {
        let updatedItem = result.object;
        if (updatedItem != null) {
          this.dailyTrip = updatedItem;
          this._alertService.showSuccess("Success");
          this.reloadForm();
        } else {
          this._alertService.displayErrorResult(result.errorCollection);
        }
      });
  }

  isControlInvalid(controlName: string) {
    let control = this.form.controls[controlName];
    if (control == undefined) {
      return false;
    }
    return control.invalid && (control.dirty || control.touched);
  }

  addExpense(source?: DailyTripExpense) {
    if (source === undefined || source === null) {
      source = new DailyTripExpense();
    }

    this.dailyTripExpenses.push(this.createExpenseForm(source));
  }

  onAddExpenseClick(event) {
    this.addExpense();
  }

  onRemoveExpenseClick(index: number) {
    this.dailyTripExpenses.removeAt(index);
  }

  print() {
    window.open(
      `${window.location.origin}/${APP_ROUTES.dailyTripTicket.replace(
        ":id",
        this.dailyTrip.id.toString()
      )}`,
      "_blank"
    );
  }

  computeTotal() {
    var values = this.form.value;

    if(values.dailyTripTypeId == EDailyTripType.Import){
      this.totalEmpty = 0;
      this.totalLoaded = values.cashDiesel + values.karga + values.xray + values.manPower
        + values.escort + values.lagay + values.remarksFee;
        //+ (values.liters * values.pricePerLiter);
      return;
    }

    if(values.dailyTripTypeId == EDailyTripType.Export){
      this.totalEmpty = 0;
      this.totalLoaded = values.cashDiesel + values.soli + values.dagSoli
        + values.areglo + values.lagay + values.remarksFee;
        // + (values.liters * values.pricePerLiter); 
      return;
    }

    if(values.dailyTripTypeId == EDailyTripType.Domestic){
      this.totalEmpty = 0;
      this.totalLoaded = values.cashDiesel + values.dagSoli + values.areglo + values.lagay + values.remarksFee;
        // + (values.liters * values.pricePerLiter); 
      return;
    }

    if(values.dailyTripTypeId == EDailyTripType.ReturnEmpty){
      this.totalLoaded = 0;
      this.totalEmpty = values.cashDieselEmpty + values.remarksFee + values.soli + values.dagSoli;
        // + (values.litersEmpty * values.pricePerLiterEmpty);
      return;
    }
  }

  isTypeImport(){
    return this.form.value.dailyTripTypeId == EDailyTripType.Import;
  }

  isTypeExport(){
    return this.form.value.dailyTripTypeId == EDailyTripType.Export;
  }

  isTypeDomestic(){
    return this.form.value.dailyTripTypeId == EDailyTripType.Domestic;
  }

  isTypeReturnEmpty() {
    return this.form.value.dailyTripTypeId == EDailyTripType.ReturnEmpty;
  }

  autofillForm(){
    const values = this.form.value;
    if(!values.tripId) return;

    this._tripService.getTrip(values.tripId)
    .subscribe((trip) => {
      if(!trip) return;
      
      if(trip.tripTypeId !== EDailyTripType.Import){
        this.form.patchValue({ dailyTripTypeId: trip.tripTypeId });
        return;
      }

      const containerId = this.form.get('tripContainerDetailId').value;
      const container = trip.tripContainers.find(x => x.id == containerId);

      let dailyTripTypeId = undefined;
      if(container){
        dailyTripTypeId = container.dailyTripDate ? EDailyTripType.ReturnEmpty : EDailyTripType.Import
        this.form.patchValue({ dailyTripTypeId: dailyTripTypeId });

        if(dailyTripTypeId == EDailyTripType.ReturnEmpty){
          this.autofillForReturnEmpty(trip.id, containerId);
        }
      } 
      
      this.autofillExpense(values.clientId, trip.shippingLineId, values.returnDestination);
    });    
  }

  autofillExpense(clientId: number, shippingLineId: number, returnDestination: string){
      this._dailyTripService
        .getDailyTripsList({
          pageNumber: 1,
          pageSize: 1,
          pageTotal: 0,
          searchFilter: {          
            clientId: clientId,
            shippingLineId: shippingLineId,
            returnDestination: returnDestination
          }
        })
        .subscribe((result) => {
          if (result.data.length === 0) return;

          this.hasReferencedBaseExpense = true;          
          this.form.patchValue({
            liters: result.data[0].liters,
            karga: result.data[0].karga,
            soli: result.data[0].soli,
            dagSoli: result.data[0].dagSoli,
            areglo: result.data[0].areglo,
            escort: result.data[0].escort,
            shippingLineAgent: result.data[0].shippingLineAgent,
            shippingLineFee: result.data[0].shippingLineFee,
            checkerName: result.data[0].checkerName,
            cFee: result.data[0].cFee,
            tollGate: result.data[0].tollGate,
            manPower: result.data[0].manPower,
            singitPier2: result.data[0].singitPier2,
            remarks: result.data[0].remarks,
            remarksFee: result.data[0].remarksFee,
            allowance: result.data[0].allowance,
            cashDiesel: result.data[0].cashDiesel,
            tollGateEmpty: result.data[0].tollGateEmpty,
            litersEmpty: result.data[0].litersEmpty,
            pricePerLiterEmpty: result.data[0].pricePerLiterEmpty,
            cashDieselEmpty: result.data[0].cashDieselEmpty,
            xray: result.data[0].xray,
            abonoDagSoli: result.data[0].abonoDagSoli,
            abonoEscort: result.data[0].abonoEscort,
            abonoManPower: result.data[0].abonoManPower,
            tabs: result.data[0].tabs,
            tabsPenalty: result.data[0].tabsPenalty,
            abonoTabs: result.data[0].abonoTabs,
            abonoTabsPenalty: result.data[0].abonoTabsPenalty,
          }, { emitEvent: true });
        });  
  }

  autofillForReturnEmpty(tripId: number, tripContainerDetailId: number){
    this._dailyTripService
      .getDailyTripsList({
        pageNumber: 1,
        pageSize: 100,
        pageTotal: 100,
        searchFilter: {          
          tripId: tripId,
          dailyTripTypeId: ETripType.Import,
        }
      })
      .subscribe((result) => {
        const matchDailyTrips = result.data.filter(x => x.tripContainerDetailId == tripContainerDetailId);
        if(matchDailyTrips.length === 0) return;

        const matchDailyTrip = matchDailyTrips[0];     
        this.form.patchValue({          
          driverId: matchDailyTrip.driverId,
          helperId: matchDailyTrip.helperId,     
          vehicleId: matchDailyTrip.vehicleId,     
        }, { emitEvent: true });
      });  
}
}



// WEBPACK FOOTER //
// ./src/app/components/trips/daily-trip-form/daily-trip-form.component.ts